import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, message } from 'antd';
import { AndroidOutlined, AppleOutlined } from '@ant-design/icons';

import {
  detectVoicePlatform,
  getVoiceSetupGuide,
  openSiriShortcutInstaller,
  type VoicePlatform,
} from '../../ai/supportChatVoiceAssistant';

type VoiceAssistantSetupPanelProps = {
  onComplete?: () => void;
  completeLabel?: string;
  autoInstall?: boolean;
};

const VoiceAssistantSetupPanel: React.FC<VoiceAssistantSetupPanelProps> = ({
  onComplete,
  completeLabel = 'Continue to SOC',
  autoInstall = true,
}) => {
  const detected = detectVoicePlatform();
  const [platform] = useState<VoicePlatform>(detected === 'other' ? 'ios' : detected);
  const [siriOpened, setSiriOpened] = useState(false);
  const autoRanRef = useRef(false);

  const guide = useMemo(() => getVoiceSetupGuide('book a service', platform), [platform]);

  useEffect(() => {
    if (!autoInstall || autoRanRef.current) return;
    if (platform !== 'ios') return;
    autoRanRef.current = true;
    const timer = window.setTimeout(() => {
      openSiriShortcutInstaller('book');
      setSiriOpened(true);
    }, 500);
    return () => window.clearTimeout(timer);
  }, [autoInstall, platform]);

  const handleInstallSiri = () => {
    openSiriShortcutInstaller('book');
    setSiriOpened(true);
    message.info('Shortcuts opened — tap Add Shortcut, then Add to Siri.');
  };

  return (
    <section className="soc-ai-voice-setup soc-ai-voice-setup--install" aria-label="Voice assistant setup">
      <p className="soc-ai-voice-setup-intro">
        {platform === 'ios'
          ? 'We pre-configure your Siri shortcut at install — opens SOC with “book a service”.'
          : platform === 'android'
            ? 'Voice shortcuts were added to your home-screen icon when you installed SOC.'
            : 'Voice shortcuts are configured at install time.'}
      </p>

      <div className="soc-ai-voice-setup-head">
        <strong>{guide.title}</strong>
      </div>

      <p className="soc-ai-voice-setup-phrase">{guide.wakePhrase}</p>

      <ol className="soc-ai-voice-setup-steps">
        {guide.steps.map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>

      {platform === 'ios' ? (
        <div className="soc-ai-voice-setup-actions">
          <Button type="primary" block icon={<AppleOutlined />} onClick={handleInstallSiri}>
            {siriOpened ? 'Open Shortcuts again' : 'Install Siri shortcut'}
          </Button>
          <p className="soc-ai-voice-setup-note">
            Pre-set URL: <code>/home?socAi=book%20a%20service</code> — no manual setup.
          </p>
        </div>
      ) : platform === 'android' ? (
        <div className="soc-ai-voice-setup-actions">
          <p className="soc-ai-voice-setup-note">
            <AndroidOutlined /> {guide.note}
          </p>
        </div>
      ) : null}

      {guide.note && platform !== 'android' ? (
        <p className="soc-ai-voice-setup-note">{guide.note}</p>
      ) : null}

      {onComplete ? (
        <div className="soc-ai-voice-setup-actions">
          <Button type={platform === 'ios' ? 'default' : 'primary'} block onClick={onComplete}>
            {completeLabel}
          </Button>
          <Button type="link" block onClick={onComplete}>
            Skip for now
          </Button>
        </div>
      ) : null}
    </section>
  );
};

export default VoiceAssistantSetupPanel;
